'use client';

import { useCallback, useEffect, useRef } from 'react';
import { usePathname } from 'next/navigation';
import { useTranslations } from 'next-intl';
import Modal from '@/shared/components/overlay/modal';
import { Button } from '@/shared/components/primitives/button';
import { readClientCookie } from '@/shared/utils/client-cookies';
import { VERIFICATION_LAST_RESEND_COOKIE } from '@/features/auth/shared/helpers/constants/verification-cookies';
import { LoginFormContainer } from '@/features/auth/internal/components/login/login-form-container';
import { CreateAccountForm } from '@/features/auth/shared/components/create-account/create-account-form';
import { VerifyEmailContent } from '@/features/auth/internal/components/verify-email/verify-email-content';
import {
  AUTH_GATE_MODAL_KEY,
  closeAuthGate,
  useAuthGateStore,
} from '@/features/auth/shared/state/auth-gate.store';

const LOGIN_TITLE_ID = 'auth-gate-login-title';
const REGISTER_TITLE_ID = 'auth-gate-register-title';

export const AuthGateModal = () => {
  const t = useTranslations('Auth.authGate');
  const pathname = usePathname();
  const view = useAuthGateStore((s) => s.view);
  const prefillEmail = useAuthGateStore((s) => s.prefillEmail);
  const hideSocial = useAuthGateStore((s) => s.hideSocial);
  const redirectUrl = useAuthGateStore((s) => s.redirectUrl);
  const pendingAction = useAuthGateStore((s) => s.pendingAction);
  const setView = useAuthGateStore((s) => s.setView);
  const setPrefillEmail = useAuthGateStore((s) => s.setPrefillEmail);

  const initialPathname = useRef(pathname);
  useEffect(() => {
    if (pathname === initialPathname.current) return;
    initialPathname.current = pathname;
    closeAuthGate();
  }, [pathname]);

  const handleAuthenticated = useCallback(() => {
    pendingAction?.();
    closeAuthGate();
  }, [pendingAction]);

  const showVerificationNotice = useCallback(
    (email: string) => {
      setPrefillEmail(email);
      setView('verifySent');
    },
    [setPrefillEmail, setView],
  );

  const title = view === 'register' || view === 'verifySent' ? t('registerTitle') : t('loginTitle');
  const titleId = view === 'login' ? LOGIN_TITLE_ID : REGISTER_TITLE_ID;
  const contentClassName = view === 'verifySent' ? 'px-2 pb-6' : 'px-2 py-6';

  return (
    <Modal
      modalKey={AUTH_GATE_MODAL_KEY}
      closeOnBackdropClick
      onClose={closeAuthGate}
      header={
        <h2 id={titleId} className='font-heading flex-1 text-lg'>
          {title}
        </h2>
      }
    >
      <div className={contentClassName}>
        {view === 'login' && (
          <LoginFormContainer
            titleId={titleId}
            redirectUrl={redirectUrl}
            hideSocialButtons={hideSocial}
            defaultEmail={prefillEmail}
            onSuccess={handleAuthenticated}
            onSwitchToRegister={() => setView('register')}
            onEmailNotVerified={showVerificationNotice}
          />
        )}

        {view === 'register' && (
          <CreateAccountForm
            titleId={titleId}
            redirectUrl={redirectUrl}
            hideSocialButtons={hideSocial}
            defaultEmail={prefillEmail}
            onSuccess={showVerificationNotice}
            onSwitchToLogin={() => setView('login')}
          />
        )}

        {view === 'verifySent' && (
          <VerifyEmailContent
            initialEmail={prefillEmail || null}
            verificationError={null}
            initialResendStatus={
              readClientCookie(VERIFICATION_LAST_RESEND_COOKIE) ? 'cooldown' : 'idle'
            }
            extraContent={
              <Button type='button' variant='secondary' size='md' onClick={() => setView('login')}>
                {t('alreadyVerified')}
              </Button>
            }
          />
        )}
      </div>
    </Modal>
  );
};
